/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { Text, VisuallyHidden, VStack } from "@microbit/ui";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { blank } from "../bt-pattern-utils";
import BluetoothPatternInput from "./BluetoothPatternInput";
import ConnectContainerDialog, {
  ConnectContainerDialogProps,
} from "./ConnectContainerDialog";
import { isNativePlatform } from "../platform";
import DialogFooterLink from "./DialogFooterLink";

export interface EnterBluetoothPatternDialogProps
  extends Omit<ConnectContainerDialogProps, "children" | "headingId"> {
  onChangeMicrobitName: (name: string) => void;
  microbitName: string | undefined;
}

const EnterBluetoothPatternDialog = ({
  onNextClick,
  onBackClick,
  onChangeMicrobitName,
  microbitName,
  ...props
}: EnterBluetoothPatternDialogProps) => {
  const [showInvalid, setShowInvalid] = useState<boolean>(false);
  const [value, setValue] = useState<string | undefined>(microbitName);

  // Native reconnection with a stored pattern uses "confirm" copy;
  // all other cases (web bluetooth, first-time native) use "copy" copy.
  const [headingId, subtitleId] =
    isNativePlatform() && microbitName
      ? ["connect-native-pattern-heading", "connect-native-pattern-subtitle"]
      : ["connect-pattern-heading", "connect-pattern-subtitle"];

  const handleNextClick = useCallback(() => {
    if (!value || value.includes(blank)) {
      setShowInvalid(true);
      return;
    }
    onChangeMicrobitName(value);
    onNextClick?.();
  }, [onChangeMicrobitName, onNextClick, value]);

  const handleBackClick = useCallback(() => {
    setShowInvalid(false);
    onBackClick?.();
  }, [onBackClick]);

  const handleNameChange = useCallback((name: string) => {
    setValue(name);
    setShowInvalid(false);
  }, []);

  return (
    <ConnectContainerDialog
      onNextClick={handleNextClick}
      onBackClick={handleBackClick}
      headingId={headingId}
      footerLeft={
        isNativePlatform() ? (
          <DialogFooterLink onClick={handleBackClick}>
            <FormattedMessage id="connect-cannot-see-pattern" />
          </DialogFooterLink>
        ) : undefined
      }
      {...props}
    >
      <VStack gap={10}>
        <Text width="100%">
          <FormattedMessage id={subtitleId} />
        </Text>
        <VStack>
          <BluetoothPatternInput
            onChange={handleNameChange}
            invalid={showInvalid}
            microbitName={value}
          />
          <VStack>
            <Text color="red" opacity={showInvalid ? 1 : 0} aria-hidden>
              <FormattedMessage id="connect-bluetooth-invalid-pattern" />
            </Text>
            <VisuallyHidden role="alert">
              {showInvalid && (
                <FormattedMessage id="connect-bluetooth-invalid-pattern" />
              )}
            </VisuallyHidden>
          </VStack>
        </VStack>
      </VStack>
    </ConnectContainerDialog>
  );
};

export default EnterBluetoothPatternDialog;
